import { Component, OnInit, Injector } from '@angular/core';
import { BaseComponent } from 'src/app/shared/baseComponent';
import { environment } from "../../../environments/environment";
import { _Object, GetObjectTaggingCommand, ListObjectsV2Command, S3Client } from "@aws-sdk/client-s3";
import { S3File, S3FileTag } from "../../../models/s3file";

interface ExtendedS3File extends S3File {
  GLBSize: number;
}

@Component({
  selector: 'app-assetlist',
  templateUrl: './assetlist.component.html',
  styleUrls: ['./assetlist.component.css']
})
export class AssetlistComponent extends BaseComponent implements OnInit {
  private readonly s3Client: S3Client;
  pictureFiles: ExtendedS3File[] = [];
  filteredPictureFiles: ExtendedS3File[] = [];
  searchFilename: string = '';
  searchTags: string = '';
  availableTagsTooltip: string = '';
  currentSortColumn: string = '';
  currentSortDirection: 'asc' | 'desc' = 'asc';
  showInfo: boolean = false;
  showPreview: string | null = null;
  selectedImage: ExtendedS3File | null = null;

  constructor(injector: Injector) {
    super(injector);
    this.s3Client = new S3Client({
      region: environment.virtexAssets.region,
      credentials: {
        accessKeyId: environment.virtexAssets.access,
        secretAccessKey: environment.virtexAssets.secret
      }
    });
  }

  async ngOnInit(): Promise<void> {
    this.loader.show();
    await this.listPictureFiles();
    this.generateAvailableTagsTooltip();
    this.applyFilters();
    this.loader.hide();
  }

  private async listPictureFiles() {

    let bucketName = environment.virtexAssets.bucket;

    try {
      const response = await this.s3Client.send(new ListObjectsV2Command({
        Bucket: bucketName,
        Prefix: "meta/"
      }));

      let contents = response.Contents;

      if (contents && contents.length > 0) {
        const files = this.excludeDirectories(contents);

        for (const file of files) {
          if (this.isImageFile(file.Key!!)) {
            const glbFileName = this.transformKeyToGlb(file.Key!!);
            const glbInfo = await this.getGlbFileInfo(bucketName, glbFileName);
            
            let pictureFile = {
              Key: file.Key!!,
              Size: file.Size!! / 1000, // Convert to KB
              GLBFile: glbInfo.exists ? glbFileName : '',
              GLBSize: glbInfo.size,
              LastModified: file.LastModified!!,
              StorageClass: file.StorageClass!!,
              ETag: file.ETag!!,
              CanPreview: true,
              Tags: await this.getTags(bucketName, file.Key!!),
              DownloadUrl: this.generateDownloadUrl(file.Key!!),
              BucketUrl: this.generateBucketUrl(bucketName, file.Key!!)
            };
            this.pictureFiles.push(pictureFile);
          }
        }
      }

    } catch (error) {
      console.error('Error listing picture files:', error);
      this.alert.warning('Failed to load picture files from S3');
    }
  }

  private excludeDirectories(files: _Object[]): _Object[] {
    return files.filter(file => {
      let isDirectory = file.Key?.endsWith('/');
      return !isDirectory;
    });
  }

  private isImageFile(filename: string): boolean {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg', '.tiff'];
    const lowercasedFilename = filename.toLowerCase();
    return imageExtensions.some(extension => lowercasedFilename.endsWith(extension));
  }

  private generateDownloadUrl(key: string): string {
    return `${environment.virtexAssets.downloadUrl}/${key}`;
  }

  private generateBucketUrl(bucketName: string, key: string): string {
    return `${environment.virtexAssets.bucketUrl}/${bucketName}?region=${environment.virtexAssets.region}&bucketType=general&prefix=${key}`;
  }

  private async getTags(bucketName: string, key: string): Promise<S3FileTag[]> {
    try {
      const tagSet = (await this.s3Client.send(
        new GetObjectTaggingCommand({
          Bucket: bucketName,
          Key: key
        })
      ))?.TagSet;

      const s3Tags: S3FileTag[] = [];

      if (!tagSet) {
        console.log(`No tags for object ${key}`);
      } else {
        for (const tag of tagSet) {
          s3Tags.push({
            Key: tag.Key,
            Value: tag.Value
          });
        }
      }

      return s3Tags;
    } catch (error) {
      console.log(`Error getting tags for ${key}:`, error);
      return [];
    }
  }

  private transformKeyToGlb(key: string): string {
    // Extract the file name without the folder paths (meta/)
    const fileName = key.substring(key.lastIndexOf('/') + 1);
    // Replace the file extension with .glb and prepend assets/ folder
    const glbFileName = fileName.replace(/\.[^/.]+$/, '.glb');
    return `assets/${glbFileName}`;
  }

  private async getGlbFileInfo(bucketName: string, glbKey: string): Promise<{exists: boolean, size: number}> {
    try {
      const response = await this.s3Client.send(new ListObjectsV2Command({
        Bucket: bucketName,
        Prefix: glbKey,
        MaxKeys: 1
      }));
      
      if (response.Contents && response.Contents.length > 0 && 
          response.Contents[0].Key === glbKey) {
        return {
          exists: true,
          size: (response.Contents[0].Size || 0) / 1000 // Convert to KB
        };
      }
      
      return { exists: false, size: 0 };
    } catch (error) {
      console.log(`Error checking GLB file ${glbKey}:`, error);
      return { exists: false, size: 0 };
    }
  }

  getFileName(key: string): string {
    return key.substring(key.lastIndexOf('/') + 1);
  }

  getFileExtension(key: string): string {
    const fileName = this.getFileName(key);
    const dotIndex = fileName.lastIndexOf('.');
    return dotIndex > 0 ? fileName.substring(dotIndex + 1).toLowerCase() : '';
  }

  getSizeFormatted(size: number): string {
    if (size < 1024) {
      return `${size.toFixed(2)} KB`;
    } else {
      const megabytes = size / 1024;
      return `${megabytes.toFixed(2)} MB`;
    }
  }

  getTagsDisplay(tags: S3FileTag[]): string {
    if (!tags || tags.length === 0) {
      return 'No tags';
    }
    return tags.map(tag => `${tag.Key}: ${tag.Value}`).join(', ');
  }

  applyFilters(): void {
    this.filteredPictureFiles = this.pictureFiles.filter(file => {
      const fileName = this.getFileName(file.Key).toLowerCase();
      const fileNameMatch = !this.searchFilename || fileName.includes(this.searchFilename.toLowerCase());
      
      let tagMatch = true;
      if (this.searchTags) {
        const searchTagsLower = this.searchTags.toLowerCase();
        const fileTagsText = file.Tags.map(tag => `${tag.Key}:${tag.Value}`).join(' ').toLowerCase();
        tagMatch = fileTagsText.includes(searchTagsLower);
      }
      
      return fileNameMatch && tagMatch;
    });
    
    // Apply current sorting after filtering
    this.applySorting();
  }

  onSearchFilenameChange(): void {
    this.applyFilters();
  }

  onSearchTagsChange(): void {
    this.applyFilters();
  }

  clearFilters(): void {
    this.searchFilename = '';
    this.searchTags = '';
    this.applyFilters();
  }

  generateAvailableTagsTooltip(): void {
    const tagCounts = new Map<string, number>();
    
    // Count occurrences of each tag key:value pair
    this.pictureFiles.forEach(file => {
      file.Tags.forEach(tag => {
        const tagKey = `${tag.Key}: ${tag.Value}`;
        tagCounts.set(tagKey, (tagCounts.get(tagKey) || 0) + 1);
      });
    });

    // Sort tags by occurrence count (descending) and then alphabetically
    const sortedTags = Array.from(tagCounts.entries())
      .sort((a, b) => {
        if (b[1] !== a[1]) return b[1] - a[1]; // Sort by count descending
        return a[0].localeCompare(b[0]); // Then alphabetically
      });

    // Generate tooltip text
    if (sortedTags.length === 0) {
      this.availableTagsTooltip = 'No tags found in any files';
    } else {
      const tagLines = sortedTags.map(([tag, count]) => `${tag} (${count})`);
      this.availableTagsTooltip = `Available tags:\n${tagLines.join('\n')}`;
    }
  }

  sortBy(column: string): void {
    if (this.currentSortColumn === column) {
      // Toggle direction if same column
      this.currentSortDirection = this.currentSortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      // New column, start with ascending
      this.currentSortColumn = column;
      this.currentSortDirection = 'asc';
    }
    this.applySorting();
  }

  private applySorting(): void {
    if (!this.currentSortColumn) {
      return;
    }

    this.filteredPictureFiles.sort((a, b) => {
      let valueA: any;
      let valueB: any;

      switch (this.currentSortColumn) {
        case 'filename':
          valueA = this.getFileName(a.Key).toLowerCase();
          valueB = this.getFileName(b.Key).toLowerCase();
          break;
        case 'imagesize':
          valueA = a.Size;
          valueB = b.Size;
          break;
        case 'glbsize':
          valueA = a.GLBSize;
          valueB = b.GLBSize;
          break;
        default:
          return 0;
      }

      let comparison = 0;
      if (valueA < valueB) {
        comparison = -1;
      } else if (valueA > valueB) {
        comparison = 1;
      }

      return this.currentSortDirection === 'asc' ? comparison : -comparison;
    });
  }

  getSortIcon(column: string): string {
    if (this.currentSortColumn !== column) {
      return 'bi-arrow-down-up'; // Default sort icon
    }
    return this.currentSortDirection === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down';
  }

  openImageModal(file: ExtendedS3File): void {
    this.selectedImage = file;
    // Use Bootstrap's modal API to show the modal
    const modal = new (window as any).bootstrap.Modal(document.getElementById('imageModal'));
    modal.show();
  }

  // Expose environment for template
  protected readonly environment = environment;

}